import { useState, useRef } from "react";
import { Loader2, Phone, Wrench, Cog, MapPin, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { calcDefekt, calcMount, geocodeDistance } from "@/lib/api";
import { COMPANY } from "@/lib/content";

const LOCATIONS = [
  { id: "budapest", label: "Budapesten belül" },
  { id: "autout_fout", label: "Autóút / főút" },
  { id: "autopalya", label: "Autópálya" },
  { id: "tehergk", label: "Tehergépkocsi (7,5–12 t)" },
  { id: "busz_kamion", label: "Busz, kamion (12 t+)" },
];

const WHEEL_CATEGORIES = [
  { id: "acel_13_15", label: 'Acélfelni 13"–15"', price: 4500 },
  { id: "acel_16", label: 'Acélfelni 16"', price: 5000 },
  { id: "acel_suv", label: "Acélfelni Terepjáró / SUV", price: 6000 },
  { id: "alu_13_16", label: 'Alufelni 13"–16"', price: 5000 },
  { id: "alu_17_18", label: 'Alufelni 17"–18"', price: 5500 },
  { id: "alu_19_20", label: 'Alufelni 19"–20"', price: 7000 },
  { id: "alu_21_plus", label: 'Alufelni 21"-től', price: 9000 },
  { id: "alu_suv", label: "Alufelni Terepjáró / SUV", price: 7000 },
];

function fmt(n) {
  return new Intl.NumberFormat("hu-HU").format(n) + " Ft";
}

function Toggle({ active, onClick, children, testId, size = "md" }) {
  const pad = size === "sm" ? "px-3 py-2 text-xs" : "px-4 py-3 text-sm";
  return (
    <button
      data-testid={testId}
      type="button"
      onClick={onClick}
      className={`${pad} font-bold uppercase tracking-wider transition-all rounded-sm border text-left ${
        active
          ? "bg-gradient-to-r from-blue-600 to-violet-600 border-violet-400 text-white glow-blue"
          : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-400 hover:text-gray-900"
      }`}
    >
      {children}
    </button>
  );
}

function AddressField({ address, setAddress, distance, resolving, onResolve, error, inputRef }) {
  const handleResolveClick = () => {
    // Blur input to close mobile keyboard
    if (inputRef?.current) inputRef.current.blur();
    onResolve();
  };
  return (
    <div className="mb-6 min-w-0">
      <label className="block text-xs uppercase tracking-[0.2em] text-gray-400 mb-3 font-bold">
        Cím / helyszín
      </label>
      <div className="flex flex-col sm:flex-row gap-2 min-w-0">
        <div className="relative flex-1 min-w-0">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-violet-600 pointer-events-none" />
          <input
            ref={inputRef}
            data-testid="calc-address-input"
            type="text"
            inputMode="text"
            autoComplete="street-address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleResolveClick();
              }
            }}
            placeholder="pl. Budapest, Váci út 1"
            className="w-full pl-10 pr-3 py-3.5 bg-gray-50 border border-gray-200 rounded-sm text-gray-900 placeholder:text-gray-300 focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-400 transition-all font-medium text-base"
          />
        </div>
        <button
          data-testid="calc-address-resolve"
          type="button"
          onClick={handleResolveClick}
          disabled={resolving || !address.trim()}
          className="px-5 py-3.5 bg-violet-500/20 border border-violet-400/40 hover:bg-violet-500/30 text-gray-900 font-bold text-xs uppercase tracking-wider rounded-sm transition-all disabled:opacity-50 whitespace-nowrap flex items-center justify-center gap-2 flex-shrink-0"
        >
          {resolving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 sm:hidden" /> Ellenőrzés</>}
        </button>
      </div>

      {distance && !error && (
        <div
          data-testid="calc-address-resolved"
          className="mt-3 flex items-start gap-3 p-3 border border-violet-500/30 bg-violet-500/5 rounded-sm min-w-0"
        >
          <Check className="w-4 h-4 text-violet-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-gray-600 leading-relaxed flex-1 min-w-0">
            <div className="text-gray-400 break-words overflow-hidden text-ellipsis line-clamp-2">
              {distance.resolved_address}
            </div>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1 min-w-0">
              <span className="font-display text-gray-900 font-bold">
                {distance.distance_km} km
              </span>
              <span className="text-gray-400">
                (~{Math.round(distance.duration_min)} perc)
              </span>
              {distance.is_budapest ? (
                <span className="inline-block px-2 py-0.5 text-[9px] uppercase tracking-wider bg-green-500/20 border border-green-500/40 text-green-600 rounded-sm">
                  Budapest
                </span>
              ) : (
                <span className="inline-block px-2 py-0.5 text-[9px] uppercase tracking-wider bg-amber-500/20 border border-amber-500/40 text-amber-300 rounded-sm">
                  Budapesten kívül
                </span>
              )}
            </div>
            {distance.is_budapest ? (
              <div className="mt-2 flex items-start gap-2 text-[11px] text-green-600 font-semibold uppercase tracking-wider">
                <Check className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                <span className="break-words">Km díj nincs — Budapesten belül</span>
              </div>
            ) : (
              <div className="mt-2 text-[11px] text-amber-200/90 leading-snug break-words">
                Kiszállás: <span className="font-bold">{distance.distance_km} km × 2 × 300 Ft</span> = <span className="font-bold text-gray-900">{new Intl.NumberFormat("hu-HU").format(Math.round(distance.distance_km * 2 * 300))} Ft</span>
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3 flex items-start gap-3 p-3 border border-red-500/30 bg-red-500/5 rounded-sm min-w-0">
          <AlertCircle className="w-4 h-4 text-red-300 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-red-200 min-w-0 break-words">{error}</div>
        </div>
      )}

      <div className="mt-2 text-[11px] text-gray-400">
        A km-t automatikusan számoljuk BP 21. ker. Csepel telephelyünktől.
      </div>
    </div>
  );
}

export default function Calculator() {
  const [mode, setMode] = useState("defekt"); // 'defekt' | 'mount'
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Defekt state
  const [dayType, setDayType] = useState("weekday");
  const [locationType, setLocationType] = useState("budapest");

  // Mount state
  const [wheelCategory, setWheelCategory] = useState("alu_17_18");
  const [wheelCount, setWheelCount] = useState(4);

  // Shared address state
  const [address, setAddress] = useState("");
  const [distance, setDistance] = useState(null); // { resolved_address, distance_km, duration_min, is_budapest }
  const [resolving, setResolving] = useState(false);
  const [addrError, setAddrError] = useState("");
  const inputRef = useRef(null);
  const controlsRef = useRef(null);

  const resolveAddress = async () => {
    if (!address.trim()) return;
    setResolving(true);
    setAddrError("");
    setDistance(null);
    try {
      const data = await geocodeDistance(address.trim());
      setDistance(data);
      // Auto-sync defekt location type based on Budapest detection
      if (mode === "defekt") {
        if (data.is_budapest && locationType === "budapest") {
          // stays budapest
        } else if (!data.is_budapest && locationType === "budapest") {
          setLocationType("autout_fout");
        }
      }
      toast.success(`Cím megtalálva — ${data.distance_km} km`);
      // Auto-scroll to the mode/parameter controls so mobile user sees them.
      // Use window scroll + rect (not scrollIntoView) to avoid horizontal shifts.
      setTimeout(() => {
        const el = controlsRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const targetY = window.scrollY + rect.top - 80; // account for sticky navbar (~80px)
        window.scrollTo({ top: targetY, behavior: "smooth" });
        // Reset any accidental horizontal scroll on <main>
        const main = document.querySelector("main");
        if (main && main.scrollLeft !== 0) main.scrollLeft = 0;
        document.documentElement.scrollLeft = 0;
      }, 200);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Nem sikerült a cím feldolgozása.";
      setAddrError(detail);
      toast.error(detail);
    } finally {
      setResolving(false);
    }
  };

  const handleCalc = async () => {
    // Distance is required only for non-Budapest defekt or non-Budapest mount
    const kmForSurcharge =
      distance && !distance.is_budapest ? distance.distance_km : 0;

    setLoading(true);
    setResult(null);
    try {
      let data;
      if (mode === "defekt") {
        data = await calcDefekt({
          day_type: dayType,
          location_type: locationType,
          distance_km: kmForSurcharge,
        });
      } else {
        data = await calcMount({
          wheel_category: wheelCategory,
          wheel_count: Number(wheelCount),
          outside_budapest: !!(distance && !distance.is_budapest),
          distance_km: kmForSurcharge,
        });
      }
      setResult(data);
      toast.success("Árajánlat elkészült!");
    } catch (e) {
      console.error(e);
      toast.error("Hiba történt. Próbáld újra.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="arkalkulator"
      data-testid="calculator-section"
      className="relative py-16 md:py-32 bg-gray-100 border-y border-gray-200"
    >
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-10 min-w-0">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 min-w-0">
          <div className="lg:col-span-5 min-w-0">
            <div className="text-xs uppercase tracking-[0.3em] text-violet-600 font-bold mb-4">
              [ 02 ] · Árkalkulátor
            </div>
            <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl leading-[0.95]">
              Mennyibe
              <br />
              kerül<span className="text-violet-400">?</span>
            </h2>
            <p className="mt-6 text-gray-500 leading-relaxed max-w-md">
              Add meg a címet és a paramétereket, és azonnal megkapod a fix
              árajánlatot. A km-t <span className="text-gray-900 font-semibold">automatikusan</span> számoljuk a Budapest 21. kerületi telephelytől.
            </p>

            <div className="mt-8 space-y-4 text-sm text-gray-500 hidden lg:block">
              <div className="flex items-start gap-3">
                <div className="w-1 h-1 mt-2 bg-violet-400 rounded-full" />
                <div>
                  Budapesten belüli defekt:{" "}
                  <span className="text-gray-900 font-semibold">25.000 Ft + ÁFA-tól</span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1 h-1 mt-2 bg-violet-400 rounded-full" />
                <div>
                  Budapesten kívül:{" "}
                  <span className="text-gray-900 font-semibold">300 Ft/km</span>{" "}
                  kiszállási díj (oda-vissza)
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1 h-1 mt-2 bg-violet-400 rounded-full" />
                <div>
                  Minimum szerelési díj:{" "}
                  <span className="text-gray-900 font-semibold">20.000 Ft</span> (1
                  db pótkerék felrakása is)
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1 h-1 mt-2 bg-violet-400 rounded-full" />
                <div>Az árak <span className="text-gray-900 font-semibold">nettó árak</span>, 27% ÁFA-t nem tartalmaznak</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 min-w-0">
            <div className="border border-gray-200 bg-[#F0F1F5] p-4 sm:p-6 md:p-10 rounded-sm noise-overlay min-w-0">
              <div className="flex items-center justify-between mb-8 pb-5 border-b border-gray-200">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-violet-600 font-bold">
                    Control Panel
                  </div>
                  <div className="font-display text-xl font-bold mt-1">
                    Konfiguráld a szolgáltatást
                  </div>
                </div>
                <div className="font-mono text-xs text-gray-400">PT-CALC v3.0</div>
              </div>

              {/* Address input (always first) */}
              <AddressField
                address={address}
                setAddress={setAddress}
                distance={distance}
                resolving={resolving}
                onResolve={resolveAddress}
                error={addrError}
                inputRef={inputRef}
              />

              {/* Anchor for smooth scroll after address resolve */}
              <div ref={controlsRef} className="scroll-mt-24" />

              {/* Mode switch */}
              <div className="mb-6">
                <label className="block text-xs uppercase tracking-[0.2em] text-gray-400 mb-3 font-bold">
                  Szolgáltatás típusa
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    data-testid="calc-mode-defekt"
                    type="button"
                    onClick={() => { setMode("defekt"); setResult(null); }}
                    className={`px-4 py-4 font-bold uppercase tracking-wider text-sm transition-all rounded-sm border flex items-center justify-center gap-2 ${
                      mode === "defekt"
                        ? "bg-gradient-to-r from-blue-600 to-violet-600 border-violet-400 text-white glow-blue"
                        : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-400"
                    }`}
                  >
                    <Wrench className="w-4 h-4" /> Defektjavítás
                  </button>
                  <button
                    data-testid="calc-mode-mount"
                    type="button"
                    onClick={() => { setMode("mount"); setResult(null); }}
                    className={`px-4 py-4 font-bold uppercase tracking-wider text-sm transition-all rounded-sm border flex items-center justify-center gap-2 ${
                      mode === "mount"
                        ? "bg-gradient-to-r from-blue-600 to-violet-600 border-violet-400 text-white glow-blue"
                        : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-400"
                    }`}
                  >
                    <Cog className="w-4 h-4" /> Gumicsere
                  </button>
                </div>
              </div>

              {mode === "defekt" && (
                <>
                  {/* Day type */}
                  <div className="mb-6">
                    <label className="block text-xs uppercase tracking-[0.2em] text-gray-400 mb-3 font-bold">
                      Nap típusa
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <Toggle
                        testId="calc-day-weekday"
                        active={dayType === "weekday"}
                        onClick={() => setDayType("weekday")}
                      >
                        Hétköznap
                      </Toggle>
                      <Toggle
                        testId="calc-day-weekend"
                        active={dayType === "weekend"}
                        onClick={() => setDayType("weekend")}
                      >
                        Hétvége / ünnepnap
                      </Toggle>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="mb-6">
                    <label className="block text-xs uppercase tracking-[0.2em] text-gray-400 mb-3 font-bold">
                      Helyszín / járműtípus
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {LOCATIONS.map((l) => (
                        <Toggle
                          key={l.id}
                          testId={`calc-loc-${l.id}`}
                          size="sm"
                          active={locationType === l.id}
                          onClick={() => setLocationType(l.id)}
                        >
                          {l.label}
                        </Toggle>
                      ))}
                    </div>
                    <div className="mt-2 text-[11px] text-gray-400">
                      Válaszd a járműtípust vagy a helyszín típusát. Budapesten belüli címeknél nincs kiszállási díj.
                    </div>
                  </div>
                </>
              )}

              {mode === "mount" && (
                <>
                  {/* Wheel category */}
                  <div className="mb-6">
                    <label className="block text-xs uppercase tracking-[0.2em] text-gray-400 mb-3 font-bold">
                      Felni típusa
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {WHEEL_CATEGORIES.map((w) => (
                        <button
                          key={w.id}
                          type="button"
                          data-testid={`calc-wheel-${w.id}`}
                          onClick={() => setWheelCategory(w.id)}
                          className={`px-3 py-3 text-xs font-bold uppercase tracking-wider transition-all rounded-sm border text-left flex items-center justify-between gap-2 ${
                            wheelCategory === w.id
                              ? "bg-gradient-to-r from-blue-600 to-violet-600 border-violet-400 text-white glow-blue"
                              : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-400"
                          }`}
                        >
                          <span>{w.label}</span>
                          <span className="font-display font-black text-sm text-violet-600">
                            {fmt(w.price)}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Wheel count */}
                  <div className="mb-6">
                    <label className="flex items-baseline justify-between text-xs uppercase tracking-[0.2em] text-gray-400 mb-3 font-bold">
                      <span>Kerekek száma</span>
                      <span className="font-display text-violet-600 font-black text-lg normal-case tracking-normal">
                        {wheelCount} db
                      </span>
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {[1, 2, 4, 6].map((n) => (
                        <button
                          key={n}
                          type="button"
                          data-testid={`calc-count-${n}`}
                          onClick={() => setWheelCount(n)}
                          className={`py-3 text-sm font-display font-bold transition-all rounded-sm border ${
                            wheelCount === n
                              ? "bg-violet-500/20 border-violet-400 text-gray-900"
                              : "bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-400"
                          }`}
                        >
                          {n} db
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <button
                data-testid="calc-submit"
                onClick={handleCalc}
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 text-white font-bold uppercase tracking-wider text-sm rounded-sm transition-all glow-blue disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                {loading ? "Kalkuláció..." : "Árajánlat számítása"}
              </button>

              {/* Result */}
              {result && (
                <div
                  data-testid="calc-result"
                  className="mt-8 border border-violet-500/30 bg-gradient-to-br from-blue-500/5 to-violet-500/10 p-6 rounded-sm fade-up"
                >
                  <div className="text-[10px] uppercase tracking-[0.25em] text-violet-600 font-bold mb-2">
                    Becsült ár
                  </div>
                  <div className="font-display font-black text-4xl md:text-6xl tracking-tighter text-gray-900">
                    {fmt(result.total_gross)}
                    <span className="text-base font-normal text-gray-400 ml-2">
                      bruttó
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-gray-500">
                    Nettó:{" "}
                    <span className="text-gray-900 font-semibold">
                      {fmt(result.subtotal_net)}
                    </span>{" "}
                    + ÁFA (27%):{" "}
                    <span className="text-gray-900 font-semibold">
                      {fmt(result.vat)}
                    </span>
                  </div>

                  <div className="mt-5 space-y-2 border-t border-gray-200 pt-5">
                    {distance && (
                      <div className="flex items-start gap-2 text-xs text-gray-600">
                        <span className="text-violet-400 font-mono">»</span>
                        <span>
                          Címzett: {distance.resolved_address.split(",").slice(0, 3).join(",")}
                          {" — "}
                          <span className="text-gray-900 font-semibold">{distance.distance_km} km</span>
                          {" a telephelytől"}
                        </span>
                      </div>
                    )}
                    {result.breakdown.map((b, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-2 text-xs text-gray-600"
                      >
                        <span className="text-violet-400 font-mono">»</span>
                        <span>{b}</span>
                      </div>
                    ))}
                  </div>

                  <a
                    href={`tel:${COMPANY.phoneRaw}`}
                    data-testid="calc-call-cta"
                    className="mt-6 w-full inline-flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 text-white font-bold uppercase tracking-wider text-sm rounded-sm transition-all glow-blue"
                  >
                    <Phone className="w-4 h-4" /> Hívj mobilgumist (0-24) · {COMPANY.phone}
                  </a>
                  <div className="mt-3 text-xs text-center text-violet-600 font-semibold uppercase tracking-wider">
                    ⚡ Azonnal megyünk — nem kell időpont
                  </div>
                  <div className="mt-3 text-[10px] text-gray-400 text-center font-mono uppercase tracking-wider leading-relaxed">
                    {result.disclaimer}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
